import axios from 'axios';
import { getBaseUrl } from '../config/api';

// Type definitions for location data
export interface LocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

export interface LocationAddress {
  city: string;
  state: string;
  zipCode: string;
  formattedAddress: string;
}

export interface LocationDetails {
  coordinates: LocationCoordinates;
  address?: LocationAddress;
}

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

/**
 * Retrieves the user's current geographic coordinates via the browser.
 */
export async function getCurrentLocation(): Promise<LocationCoordinates | null> {
  if (!navigator.geolocation) {
    console.error('Geolocation is not supported by this browser.');
    return null;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        console.warn('Error getting current location (will use stored location if available):', error.message);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
  });
}

/**
 * Try backend reverse geocoding endpoint as fallback
 */
async function tryBackendReverseGeocode(
  latitude: number,
  longitude: number
): Promise<LocationAddress | null> {
  try {
    const API_BASE_URL = getBaseUrl();
    const response = await axios.get(`${API_BASE_URL}/location/reverse-geocode`, {
      params: { latitude, longitude },
      timeout: 10000,
    });

    if (response.data?.success && response.data?.data) {
      const address = response.data.data;
      // Validate that we got meaningful data
      if (address.city && address.state) {
        return address;
      }
    }
    return null;
  } catch (error) {
    console.error('Backend reverse geocoding failed:', error);
    return null;
  }
}

/**
 * Real reverse geocoding using Google Maps API with backend fallback.
 */
async function realReverseGeocode(
  latitude: number,
  longitude: number
): Promise<LocationAddress | null> {
  // Try frontend Google Maps API first if key is available
  if (GOOGLE_MAPS_API_KEY) {
    try {
      const response = await axios.get(
        'https://maps.googleapis.com/maps/api/geocode/json',
        {
          params: {
            latlng: `${latitude},${longitude}`,
            key: GOOGLE_MAPS_API_KEY,
            result_type: 'locality|administrative_area_level_1|postal_code',
          },
          timeout: 10000,
        }
      );

      const data = response.data;
      if (data.status === 'OK' && data.results && data.results.length > 0) {
        const result = data.results[0];
        let city = '';
        let state = '';
        let zipCode = '';

        for (const component of result.address_components) {
          const types: string[] = component.types;
          if (types.includes('locality')) city = component.long_name;
          if (types.includes('administrative_area_level_1')) state = component.short_name;
          if (types.includes('postal_code')) zipCode = component.long_name;
        }

        // Validate that we got meaningful data
        if (city && state) {
          return {
            city,
            state,
            zipCode,
            formattedAddress: result.formatted_address
          };
        }
      }
    } catch (err) {
      console.warn('Frontend reverse geocoding failed, trying backend:', err);
    }
  }

  // Fallback to backend endpoint
  console.log('Attempting backend reverse geocoding...');
  const backendResult = await tryBackendReverseGeocode(latitude, longitude);
  if (backendResult) {
    return backendResult;
  }

  // If both fail, return null (caller will use stored location fallback)
  console.warn('All reverse geocoding methods failed - will use stored location if available');
  return null;
}

/**
 * Produces mock address data for development/testing when no API key is available.
 */
export function getMockAddressFromCoordinates(
  latitude: number,
  longitude: number
): LocationAddress {
  // Example: simple mock based on rectangular zones
  if (latitude > 37 && latitude < 38 && longitude > -123 && longitude < -122) {
    return {
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94103',
      formattedAddress: 'San Francisco, CA 94103, USA'
    };
  }
  if (latitude > 40 && latitude < 41 && longitude > -75 && longitude < -73) {
    return {
      city: 'New York',
      state: 'NY',
      zipCode: '10001',
      formattedAddress: 'New York, NY 10001, USA'
    };
  }
  // Generic fallback
  const latRounded = Math.abs(Math.round(latitude * 100) / 100);
  const lngRounded = Math.abs(Math.round(longitude * 100) / 100);
  return {
    city: `City${latRounded}`,
    state: lngRounded % 2 ? 'CA' : 'NY',
    zipCode: `${Math.round(latRounded * 1000)}${Math.round(lngRounded * 10)}`,
    formattedAddress: `City${latRounded}, ${lngRounded % 2 ? 'CA' : 'NY'}, USA`
  };
}

/**
 * Fetches full details: user coords plus address info.
 */
export async function getFullLocationDetails(): Promise<LocationDetails | null> {
  const coords = await getCurrentLocation();
  if (!coords) return null;

  const address = await realReverseGeocode(coords.latitude, coords.longitude);
  
  // If reverse geocoding failed, return null instead of mock data
  if (!address) {
    console.warn('Reverse geocoding failed - will use stored location if available');
    return null;
  }
  
  // Validate that we have a real address (not a mock)
  if (address.city.startsWith('City') && /^\d+/.test(address.city.replace('City', ''))) {
    console.warn('Detected mock address - will use stored location if available');
    return null;
  }
  
  return { coordinates: coords, address };
}

/**
 * Formats coordinates as a MongoDB GeoJSON Point.
 */
export function formatCoordinatesForMongoDB(
  coords: LocationCoordinates
): { type: 'Point'; coordinates: [number, number] } {
  return { type: 'Point', coordinates: [coords.longitude, coords.latitude] };
}
